import type { Option } from '../components/Combobox.tsx'

// A course: one instruction covering a span of dates, holding however many
// medicines were prescribed together.
//
// "10th to 15th, five Kamraj and four Jeevan Viswas, morning and evening"
// is one course with two medicines -- not six days you type out, and not
// two separate instructions. A single day is simply a course whose start
// and end are the same date.
//
// The DATABASE still stores one row per medicine per day, because that is
// what the invoice is built from and what the register has to show. The
// course is the editing surface; the expansion is the record.

export type Item = {
  key: string
  product: Option | null
  /** The dose taken on EACH occasion, not the daily total. */
  quantity: string
  /** 1 once, 2 morning and evening, 3 thrice. */
  timesPerDay: number
}

export type Course = { key: string; from: string; to: string; items: Item[] }

export type Line = {
  productId: string
  quantity: number
  dosesPerDay: number
  doseDate: string | null
}

export const uid = () => crypto.randomUUID()
export const blankItem = (times = 2): Item =>
  ({ key: uid(), product: null, quantity: '1', timesPerDay: times })

export const today = () => new Date().toISOString().slice(0, 10)
export const addDays = (iso: string, n: number) =>
  new Date(new Date(iso).getTime() + n * 864e5).toISOString().slice(0, 10)

/** Inclusive: the 4th to the 6th is three days, not two. */
export const daysBetween = (from: string, to: string) =>
  Math.floor((Date.parse(to) - Date.parse(from)) / 864e5) + 1

/** Whole numbers plain, fractions to two places. */
export const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2))

/** A mistyped year must not silently generate three thousand rows. */
export const MAX_COURSE_DAYS = 120

export const dailyTotal = (i: Item) => Number(i.quantity) * i.timesPerDay
export const courseTotal = (c: Course) =>
  c.items.filter(i => i.product).reduce(
    (sum, i) => sum + dailyTotal(i) * daysBetween(c.from, c.to), 0)
export const coursePerDay = (c: Course) =>
  c.items.filter(i => i.product).reduce((sum, i) => sum + dailyTotal(i), 0)

export const eachDate = (c: Course): string[] =>
  Array.from({ length: Math.max(0, daysBetween(c.from, c.to)) }, (_, i) => addDays(c.from, i))

/**
 * Courses to the flat rows the API stores: one per medicine per day.
 *
 * Where two courses overlap on a date and name the same medicine, the
 * LATER course wins rather than both being written. Two doses of the same
 * thing on one day is almost always a correction that was meant to replace
 * the first, and silently dispensing the sum is the dangerous reading.
 */
export function expandCourses(courses: Course[]): Line[] {
  const byDateProduct = new Map<string, Line>()
  for (const c of courses) {
    const dates = eachDate(c)
    for (const item of c.items) {
      if (!item.product || !(Number(item.quantity) > 0)) continue
      for (const date of dates) {
        byDateProduct.set(`${date}|${item.product.id}`, {
          productId: item.product.id,
          quantity: Number(item.quantity),
          dosesPerDay: item.timesPerDay,
          doseDate: date,
        })
      }
    }
  }
  return [...byDateProduct.values()]
    .sort((a, b) => (a.doseDate ?? '').localeCompare(b.doseDate ?? ''))
}

/** Dates that more than one course claims for the same medicine. */
export function overlaps(courses: Course[]): string[] {
  const seen = new Map<string, number>()
  const clashes = new Set<string>()
  courses.forEach((c, ci) => {
    for (const item of c.items) {
      if (!item.product) continue
      for (const date of eachDate(c)) {
        const key = `${date}|${item.product.id}`
        const prev = seen.get(key)
        if (prev !== undefined && prev !== ci) {
          clashes.add(`${item.product.label} on ${date}`)
        }
        seen.set(key, ci)
      }
    }
  })
  return [...clashes]
}

/**
 * Flat rows back into courses, for editing an existing prescription.
 *
 * Consecutive dates carrying an identical set of medicines and doses are
 * one course. Reconstructed rather than stored, so a prescription written
 * before courses existed still opens as something editable.
 *
 * The one thing this cannot recover is two ADJACENT courses that happened
 * to carry identical instructions -- they come back merged into one. They
 * say the same thing over the same dates, so nothing about the
 * prescription changes; only the row count in the table does.
 */
export function coursesFromLines(lines: Record<string, unknown>[]): Course[] {
  const byDate = new Map<string, Item[]>()
  for (const l of lines) {
    if (!l['dose_date']) continue
    const date = String(l['dose_date']).slice(0, 10)
    const item: Item = {
      key: uid(),
      product: { id: String(l['product_id']), label: String(l['product_name']) },
      quantity: String(Number(l['quantity'])),
      timesPerDay: Number(l['doses_per_day'] ?? 1),
    }
    byDate.set(date, [...(byDate.get(date) ?? []), item])
  }

  // Order within a day must not affect whether two days are "the same".
  const signature = (items: Item[]) =>
    items.map(i => `${i.product?.id}:${i.quantity}:${i.timesPerDay}`).sort().join('|')

  const courses: Course[] = []
  for (const date of [...byDate.keys()].sort()) {
    const items = byDate.get(date)!
    const sig = signature(items)
    const last = courses[courses.length - 1]
    const contiguous = last && addDays(last.to, 1) === date && signature(last.items) === sig
    if (contiguous) last.to = date
    else courses.push({ key: uid(), from: date, to: date, items })
  }
  return courses
}
